import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { getLesson, lessonAudioUrl } from "@/lib/api";
import { getDoneFor, toggleActivity } from "@/lib/progress";
import Loading from "@/components/Loading";
import { ArrowLeft, BookOpen, Heart, Printer, Share2, CheckCircle2, Circle, MessageCircleQuestion, HandHeart, Volume2, Pause, Play } from "lucide-react";
import { toast } from "sonner";

const diffColor = {
    easy: "bg-mint",
    medium: "bg-sun",
    "big-impact": "bg-coral text-white",
};

function ageBadge(age) {
    if (age === "5-8") return "bg-mint";
    if (age === "9-12") return "bg-coral text-white";
    return "bg-sky text-white";
}

export default function LessonDetail() {
    const { id } = useParams();
    const [lesson, setLesson] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeAge, setActiveAge] = useState("5-8");
    const [done, setDone] = useState({});
    const [audioState, setAudioState] = useState("idle"); // idle | loading | playing | paused
    const audioRef = useRef(null);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        getLesson(id)
            .then((d) => {
                if (!mounted) return;
                setLesson(d);
                setDone(getDoneFor(d.id));
            })
            .catch(() => toast.error("Lesson not found"))
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, [id]);

    function toggleDone(idx) {
        if (!lesson) return;
        const result = toggleActivity(lesson, idx);
        setDone((d) => ({ ...d, [idx]: result.done }));
        if (result.done) {
            const a = lesson.community_activities[idx];
            const pts = (a?.difficulty === "big-impact") ? 20 : (a?.difficulty === "medium") ? 10 : 5;
            toast.success(`+${pts} points · "${a?.title || "Kindness done"}"`, {
                description: `Total: ${result.totals.points} pts`,
            });
        }
        if (result.newlyEarnedBadge) {
            const b = result.newlyEarnedBadge;
            toast.success(`${b.emoji} You earned the "${b.title}" badge!`, {
                description: `Visit Rewards to print your certificate.`,
                duration: 6000,
            });
        }
    }

    function handleShare() {
        if (navigator.share) {
            navigator.share({ title: lesson.title, url: window.location.href }).catch(() => {});
        } else {
            navigator.clipboard.writeText(window.location.href);
            toast.success("Link copied!");
        }
    }

    async function handleListen() {
        if (!lesson) return;
        const audio = audioRef.current;
        if (!audio) return;
        if (audioState === "playing") {
            audio.pause();
            setAudioState("paused");
            return;
        }
        if (audioState === "paused") {
            try { await audio.play(); setAudioState("playing"); } catch { /* ignore */ }
            return;
        }
        // First play — load source
        setAudioState("loading");
        try {
            audio.src = lessonAudioUrl(lesson.id);
            audio.onended = () => setAudioState("idle");
            audio.onpause = () => {
                if (!audio.ended) setAudioState((s) => (s === "playing" ? "paused" : s));
            };
            audio.onplay = () => setAudioState("playing");
            audio.onerror = () => {
                setAudioState("idle");
                toast.error("Couldn't load narration. Try again in a moment.");
            };
            await audio.play();
        } catch (e) {
            setAudioState("idle");
            toast.error("Audio playback failed.");
        }
    }

    if (loading) return <Loading />;
    if (!lesson) {
        return (
            <div className="max-w-3xl mx-auto px-5 py-20 text-center">
                <h2 className="font-heading text-3xl font-bold">Lesson not found</h2>
                <Link to="/lessons" className="btn-sticker bg-sun text-ink mt-6 inline-flex">Back to lessons</Link>
            </div>
        );
    }

    return (
        <div data-testid="lesson-detail-page">
            {/* HERO */}
            <section className="border-b-2 border-ink bg-sun/40">
                <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
                    <Link to="/lessons" className="inline-flex items-center gap-2 font-heading font-semibold mb-6 hover:underline" data-testid="back-link">
                        <ArrowLeft className="w-4 h-4" strokeWidth={2.5} /> All lessons
                    </Link>

                    <div className="flex flex-wrap items-center gap-2 mb-4">
                        <span className="pill bg-white">
                            <BookOpen className="w-3.5 h-3.5" strokeWidth={2.5} /> {lesson.theme}
                        </span>
                        <span className="pill bg-white">{lesson.bible_reference}</span>
                        {(lesson.age_groups || []).map((a) => (
                            <span key={a} className={`pill ${ageBadge(a)}`}>Ages {a}</span>
                        ))}
                        <span className="font-accent text-xl text-ink/70">{lesson.date}</span>
                    </div>

                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="lesson-title">
                        {lesson.title}
                    </h1>

                    <div className="mt-6 flex flex-wrap gap-3">
                        <button
                            onClick={handleListen}
                            className="btn-sticker bg-coral text-white text-sm"
                            data-testid="listen-btn"
                            disabled={audioState === "loading"}
                        >
                            {audioState === "loading" ? (
                                <>
                                    <Volume2 className="w-4 h-4 mr-2 animate-pulse" strokeWidth={2.5} /> Loading...
                                </>
                            ) : audioState === "playing" ? (
                                <>
                                    <Pause className="w-4 h-4 mr-2" strokeWidth={2.5} /> Pause
                                </>
                            ) : audioState === "paused" ? (
                                <>
                                    <Play className="w-4 h-4 mr-2" strokeWidth={2.5} /> Resume
                                </>
                            ) : (
                                <>
                                    <Volume2 className="w-4 h-4 mr-2" strokeWidth={2.5} /> Listen
                                </>
                            )}
                        </button>
                        <button onClick={() => window.print()} className="btn-sticker bg-white text-ink text-sm" data-testid="print-btn">
                            <Printer className="w-4 h-4 mr-2" strokeWidth={2.5} /> Print
                        </button>
                        <button onClick={handleShare} className="btn-sticker bg-mint text-ink text-sm" data-testid="share-btn">
                            <Share2 className="w-4 h-4 mr-2" strokeWidth={2.5} /> Share
                        </button>
                        <audio ref={audioRef} preload="none" data-testid="lesson-audio" />
                    </div>
                </div>
            </section>

            {/* KEY VERSE */}
            <section className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
                <div className="card-sticker p-8 bg-white" data-testid="key-verse">
                    <p className="font-accent text-2xl text-coral mb-1">today's key verse</p>
                    <p className="font-heading font-bold text-2xl">{lesson.key_verse}</p>
                    <blockquote className="mt-4 text-xl leading-relaxed border-l-4 border-coral pl-5 italic text-ink/90">
                        "{lesson.key_verse_text}"
                    </blockquote>
                </div>
            </section>

            {/* STORY */}
            <section className="max-w-5xl mx-auto px-5 sm:px-8 py-6">
                <h2 className="font-heading text-3xl font-bold tracking-tight mb-4 flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-sky border-2 border-ink">
                        <BookOpen className="w-5 h-5 text-white" strokeWidth={2.5} />
                    </span>
                    The story
                </h2>
                <div className="card-sticker p-7 bg-white">
                    <p className="text-lg leading-relaxed whitespace-pre-line">{lesson.story_summary}</p>
                </div>
            </section>

            {/* AGE-SPECIFIC EXPLANATION */}
            <section className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
                <h2 className="font-heading text-3xl font-bold tracking-tight mb-4 flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-lilac border-2 border-ink">
                        <Heart className="w-5 h-5 text-ink" strokeWidth={2.5} />
                    </span>
                    What does it mean?
                </h2>

                <div className="flex gap-2 mb-4" data-testid="age-toggle">
                    {[
                        { v: "5-8", l: "Ages 5–8", color: "bg-mint" },
                        { v: "9-12", l: "Ages 9–12", color: "bg-coral text-white" },
                    ].map((o) => (
                        <button
                            key={o.v}
                            onClick={() => setActiveAge(o.v)}
                            data-testid={`age-toggle-${o.v}`}
                            className={`pill transition-all ${activeAge === o.v ? `${o.color}` : "bg-white"}`}
                        >
                            {o.l}
                        </button>
                    ))}
                </div>

                <div className="card-sticker p-7 bg-white">
                    <p className="text-lg leading-relaxed">
                        {activeAge === "5-8" ? lesson.explanation_5_8 : lesson.explanation_9_12}
                    </p>
                </div>
            </section>

            {/* REFLECTION */}
            {lesson.reflection_questions?.length > 0 && (
                <section className="max-w-5xl mx-auto px-5 sm:px-8 py-6">
                    <h2 className="font-heading text-3xl font-bold tracking-tight mb-4 flex items-center gap-3">
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-sun border-2 border-ink">
                            <MessageCircleQuestion className="w-5 h-5 text-ink" strokeWidth={2.5} />
                        </span>
                        Talk about it
                    </h2>
                    <div className="grid md:grid-cols-3 gap-4">
                        {lesson.reflection_questions.map((q, i) => (
                            <div key={i} className="card-sticker p-6 bg-cream" data-testid={`reflection-${i}`}>
                                <span className="font-accent text-3xl text-coral">Q{i + 1}.</span>
                                <p className="font-heading font-semibold text-lg mt-1">{q}</p>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* COMMUNITY ACTIVITIES */}
            <section className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
                <h2 className="font-heading text-3xl font-bold tracking-tight mb-2 flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-mint border-2 border-ink">
                        <HandHeart className="w-5 h-5 text-ink" strokeWidth={2.5} />
                    </span>
                    Live it out this week
                </h2>
                <p className="text-ink/70 mb-6 max-w-2xl">
                    Pick one! Tap the circle to mark it done. (Always do these with a grown-up.)
                </p>

                <div className="card-sticker bg-[#FFF8E1] p-7 ruled-paper" data-testid="activities-checklist">
                    <p className="font-accent text-3xl text-coral mb-3">My kindness checklist</p>
                    <ul className="space-y-4">
                        {lesson.community_activities.map((a, i) => (
                            <li key={i} className="flex gap-4" data-testid={`activity-${i}`}>
                                <button
                                    onClick={() => toggleDone(i)}
                                    className="mt-1 shrink-0"
                                    aria-label="Toggle done"
                                    data-testid={`activity-toggle-${i}`}
                                >
                                    {done[i] ? (
                                        <CheckCircle2 className="w-7 h-7 text-mint" strokeWidth={2.5} />
                                    ) : (
                                        <Circle className="w-7 h-7 text-ink" strokeWidth={2.5} />
                                    )}
                                </button>
                                <div className="flex-1">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <h3 className={`font-heading font-bold text-xl ${done[i] ? "line-through text-ink/50" : ""}`}>
                                            {a.title}
                                        </h3>
                                        <span className={`pill ${diffColor[a.difficulty] || "bg-white"}`}>
                                            {a.difficulty}
                                        </span>
                                        <span className={`pill ${ageBadge(a.age_group)}`}>
                                            {a.age_group === "all" ? "All ages" : `Ages ${a.age_group}`}
                                        </span>
                                    </div>
                                    <p className="text-ink/80 leading-relaxed">{a.description}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* PRAYER */}
            {lesson.prayer && (
                <section className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
                    <div className="card-sticker bg-lilac p-7" data-testid="prayer-card">
                        <p className="font-accent text-3xl text-ink/80">a tiny prayer</p>
                        <p className="text-lg leading-relaxed mt-2">{lesson.prayer}</p>
                    </div>
                </section>
            )}

            <section className="max-w-5xl mx-auto px-5 sm:px-8 py-10 text-center">
                <Link to="/lessons" className="btn-sticker bg-sun text-ink" data-testid="more-lessons-btn">
                    More lessons →
                </Link>
            </section>
        </div>
    );
}
